import React from "react";
import HoverTooltip from "components/HoverTooltip/HoverTooltip";
import { css } from "@emotion/react";
import { Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ApplicationStatus, ApplicationStatusUtil } from "models/ApplicationStatus.model";
import { useTranslation } from "react-i18next";
import { formatDateStr } from "utils/formatDateStr";
import { DATE_FORMATS, MAX_USER_PENDING_ACCESS_REQUESTS_COUNT } from "constants/configs";
import { useStoreSelector } from "store/hooks";
import { selectRecentMyAccessRequests } from "store/domain-data/access-request/accessRequest";
import { AccessRequestStatus } from "models/AccessRequest.model";

type Props = {
  applicationId: number;
  applicationStatus: ApplicationStatus;
  accessRequestedDate?: string;
  hasApplicationAccess?: boolean;
  onClickOpenApplication: (applicationId: number) => void;
  onClickRequestAccess: (applicationId: number) => void;
};

const AccessRequestActionButton: React.FC<Props> = ({
  applicationId,
  applicationStatus,
  accessRequestedDate = null,
  hasApplicationAccess = false,
  onClickOpenApplication,
  onClickRequestAccess,
}) => {
  const { t } = useTranslation();
  const myAccessRequests = useStoreSelector(selectRecentMyAccessRequests);

  const pendingAccessRequests = myAccessRequests.filter(
    (accessRequest) => accessRequest.status === AccessRequestStatus.Pending
  );

  const hasReachedPendingLimit = pendingAccessRequests.length >= MAX_USER_PENDING_ACCESS_REQUESTS_COUNT;
  const isApplicationEditable = ApplicationStatusUtil.canEditApplication(applicationStatus);
  const hasAccessRequest = Boolean(accessRequestedDate);

  const disabledReason = React.useMemo(() => {
    if (!isApplicationEditable) {
      return t(`You may not request access to this project. It is {{applicationStatus}} and cannot be modified.`, {
        applicationStatus,
      });
    } else if (accessRequestedDate) {
      return t(
        `You have an active access request for this project (submitted {{accessRequestedDate}}). You can have one active request per project.`,
        { accessRequestedDate: formatDateStr(accessRequestedDate, DATE_FORMATS.FULL_DATE) }
      );
    } else if (hasReachedPendingLimit) {
      return t("Request limit reached: maximum {{maxPendingAccessRequests}} pending requests", {
        maxPendingAccessRequests: MAX_USER_PENDING_ACCESS_REQUESTS_COUNT,
      });
    }

    return "";
  }, [t, isApplicationEditable, hasReachedPendingLimit, accessRequestedDate, applicationStatus]);

  if (hasApplicationAccess) {
    return (
      <Button
        data-testid={`OpenProjectButton-${applicationId}`}
        onClick={() => onClickOpenApplication(applicationId)}
        variant={"outlined"}
        size={"medium"}
      >
        {t("Open")}
      </Button>
    );
  }

  return (
    <HoverTooltip title={disabledReason}>
      <StyledButton
        data-testid={`RequestAccessButton-${applicationId}`}
        onClick={() => onClickRequestAccess(applicationId)}
        variant={"contained"}
        color={"secondary"}
        size={"medium"}
        disabled={hasAccessRequest || !isApplicationEditable || hasReachedPendingLimit}
      >
        {t("Request access")}
      </StyledButton>
    </HoverTooltip>
  );
};

const StyledButton = styled(Button)(
  ({ theme }) => css`
    background-color: #dae8ff !important;

    &:disabled {
      background-color: ${theme.palette.grey[300]} !important;
    }
  `
);

export default AccessRequestActionButton;
